import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api, apiErr } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { toast } from "sonner";
import {
  Building2, Router, Users, Activity, ShieldCheck, ShieldX, Wifi, Clock, Ban,
  Trash2, AlertTriangle, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";

function Stat({ icon: Icon, label, value, tone = "blue", sub }) {
  const tones = {
    blue: "text-blue-400 bg-blue-500/10 ring-blue-500/20",
    emerald: "text-emerald-400 bg-emerald-500/10 ring-emerald-500/20",
    amber: "text-amber-400 bg-amber-500/10 ring-amber-500/20",
    rose: "text-rose-400 bg-rose-500/10 ring-rose-500/20",
    cyan: "text-cyan-400 bg-cyan-500/10 ring-cyan-500/20",
    slate: "text-slate-400 bg-slate-500/10 ring-slate-500/20",
  };
  return (
    <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-5 h-full flex flex-col justify-between hover:border-blue-600/30 transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center ring-1 ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-4">
        <div className="font-display text-3xl font-bold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function ResetDemoZone() {
  const [step1, setStep1] = useState(false);
  const [step2, setStep2] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [structures, setStructures] = useState([]);
  const [selStruct, setSelStruct] = useState("");
  const [structConfirm, setStructConfirm] = useState(false);
  const [structBusy, setStructBusy] = useState(false);

  useEffect(() => {
    api.get("/structures").then((r) => setStructures(r.data)).catch(() => {});
  }, []);

  const doReset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/reset-demo");
      toast.success(`Demo svuotata: ${r.data.total} elementi eliminati`);
      setStep2(false);
      setText("");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail, "Errore durante lo svuotamento"));
      setBusy(false);
    }
  };

  const doStructReset = async () => {
    setStructBusy(true);
    try {
      const r = await api.delete(`/structures/${selStruct}`);
      const tot = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Struttura eliminata (${tot} elementi collegati)`);
      setStructConfirm(false);
      setSelStruct("");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail, "Errore durante l'eliminazione"));
      setStructBusy(false);
    }
  };

  const selName = structures.find((s) => s.id === selStruct)?.name || "";

  return (
    <div className="mt-6 rounded-xl border border-rose-900/50 bg-rose-950/20 p-6" data-testid="reset-demo-zone">
      <div className="flex items-start gap-3 mb-5">
        <span className="h-9 w-9 rounded-lg flex items-center justify-center ring-1 ring-rose-500/20 bg-rose-500/10 text-rose-400 shrink-0">
          <AlertTriangle className="h-4 w-4" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-rose-200">Zona pericolosa</h3>
          <p className="text-sm text-slate-400 mt-1">Operazioni distruttive e irreversibili. Gli account amministratori non vengono mai eliminati.</p>
        </div>
      </div>

      {/* Reset per struttura */}
      <div className="rounded-lg border border-rose-900/40 bg-[#0B0F17]/40 p-4 mb-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h4 className="text-sm font-semibold text-slate-200">Reset per struttura</h4>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">Elimina una singola struttura con tutti i dati collegati (router, prenotazioni, profili, sessioni, log).</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selStruct}
              onChange={(e) => setSelStruct(e.target.value)}
              data-testid="reset-struct-select"
              className="h-9 rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 text-sm text-slate-200 focus:outline-none focus:border-rose-600 min-w-[200px]"
            >
              <option value="">Seleziona struttura…</option>
              {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <Button
              variant="destructive"
              disabled={!selStruct}
              onClick={() => setStructConfirm(true)}
              data-testid="reset-struct-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Elimina
            </Button>
          </div>
        </div>
      </div>

      {/* Svuota tutta la demo */}
      <div className="rounded-lg border border-rose-900/40 bg-[#0B0F17]/40 p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h4 className="text-sm font-semibold text-slate-200">Svuota Demo (tutto)</h4>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">Elimina TUTTE le strutture, router, prenotazioni, profili, sessioni e log per ripartire da zero.</p>
          </div>
          <Button variant="destructive" onClick={() => setStep1(true)} data-testid="reset-demo-btn" className="shrink-0">
            <Trash2 className="h-4 w-4 mr-2" /> Svuota Demo
          </Button>
        </div>
      </div>

      {/* Conferma reset struttura */}
      <AlertDialog open={structConfirm} onOpenChange={(o) => { if (!o) { setStructConfirm(false); } }}>
        <AlertDialogContent data-testid="reset-struct-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{selName}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati definitivamente la struttura <span className="font-semibold text-rose-300">{selName}</span> e
              tutti i dati collegati (router, prenotazioni, profili, sessioni e log). Azione irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-struct-cancel" disabled={structBusy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-struct-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700"
              disabled={structBusy}
              onClick={(e) => { e.preventDefault(); doStructReset(); }}
            >
              {structBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Eliminazione...</> : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Prima conferma svuota tutto */}
      <AlertDialog open={step1} onOpenChange={setStep1}>
        <AlertDialogContent data-testid="reset-demo-confirm-1">
          <AlertDialogHeader>
            <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente TUTTI i dati (strutture, router, prenotazioni,
              profili, sessioni e log). Gli amministratori restano attivi. Questa azione non
              può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-demo-cancel-1">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-demo-next-btn"
              className="bg-rose-600 hover:bg-rose-700"
              onClick={(e) => { e.preventDefault(); setStep1(false); setStep2(true); }}
            >
              Continua
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Seconda conferma con digitazione */}
      <AlertDialog open={step2} onOpenChange={(o) => { if (!o) { setStep2(false); setText(""); } }}>
        <AlertDialogContent data-testid="reset-demo-confirm-2">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma finale</AlertDialogTitle>
            <AlertDialogDescription>
              Per confermare, digita <span className="font-mono font-semibold text-rose-300">SVUOTA</span> nel campo qui sotto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="SVUOTA"
            data-testid="reset-demo-input"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-demo-cancel-2" disabled={busy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-demo-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700"
              disabled={text !== "SVUOTA" || busy}
              onClick={(e) => { e.preventDefault(); doReset(); }}
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Svuotamento...</> : "Svuota definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Dashboard() {
  const { effectiveStructure, isSuperAdmin } = useAuth();
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [traffic, setTraffic] = useState([]);

  useEffect(() => {
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/dashboard${p}`).then((r) => setD(r.data)).catch(() => {});
    api.get(`/reports/traffic${p}`).then((r) => setTraffic(r.data)).catch(() => {});
  }, [effectiveStructure]);

  if (!d) return <div className="text-slate-400">{t("common.loading")}</div>;

  const pieData = [
    { name: t("dash.auth_accept"), value: d.auth_accept, color: "#10B981" },
    { name: t("dash.auth_reject"), value: d.auth_reject, color: "#EF4444" },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={t("nav.dashboard")} subtitle={t("dash.title")} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Stat icon={Building2} label={t("dash.structures")} value={d.structures} tone="blue" />
        <Stat icon={Router} label={t("dash.routers_online")} value={`${d.routers_online}/${d.routers_total}`} tone="emerald" sub={`${d.routers_offline} offline`} />
        <Stat icon={Users} label={t("dash.users_active")} value={d.users_active} tone="cyan" />
        <Stat icon={Activity} label={t("dash.sessions_active")} value={d.sessions_active} tone="emerald" />
        <Stat icon={Clock} label={t("dash.codes_pending")} value={d.codes_pending} tone="amber" />
        <Stat icon={Wifi} label={t("dash.codes_active")} value={d.codes_active} tone="emerald" />
        <Stat icon={Ban} label={t("dash.codes_disabled")} value={d.codes_disabled} tone="rose" />
        <Stat icon={ShieldCheck} label={t("dash.auth_accept")} value={d.auth_accept} tone="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-xl border border-[#1F2937] bg-[#111827] p-6">
          <h3 className="font-display font-semibold mb-4">{t("dash.traffic_chart")}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={traffic}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
              <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickFormatter={(v) => v?.slice(5)} />
              <YAxis stroke="#6B7280" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="download" name={t("dash.download")} fill="#2563EB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="upload" name={t("dash.upload")} fill="#06B6D4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 flex flex-col">
          <h3 className="font-display font-semibold mb-2">{t("dash.auth_ratio")}</h3>
          <div className="flex-1 flex items-center justify-center">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 text-xs">
            <span className="flex items-center gap-1.5 text-emerald-400"><ShieldCheck className="h-3.5 w-3.5" /> {d.auth_accept}</span>
            <span className="flex items-center gap-1.5 text-rose-400"><ShieldX className="h-3.5 w-3.5" /> {d.auth_reject}</span>
          </div>
        </div>
      </div>

      {isSuperAdmin && <ResetDemoZone />}
    </div>
  );
}
